/**
 * EntityFormModal - generic create/edit modal for Staffing CRM entities.
 *
 * The parent owns the form state; this component only renders controlled
 * inputs from a `fields` config. It is module-level (never defined inside a
 * render body) so inputs keep focus while the user types.
 *
 * Field config: { key, label, type?: 'text'|'number'|'date'|'select'|'account'|'textarea',
 *                 options?: string[], required?: bool, full?: bool, placeholder?: string }
 */
import { Modal } from '../ui/Modal';

const LABEL = {
  display: 'block',
  marginBottom: 4,
  fontSize: 10,
  fontWeight: 800,
  color: '#666',
  letterSpacing: 1,
  textTransform: 'uppercase',
};
const INPUT = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #D8DED4',
  background: '#fff',
  fontSize: 12.5,
  fontFamily: 'inherit',
  color: '#0D0D0D',
};
const BTN_GHOST = {
  padding: '8px 16px',
  borderRadius: 6,
  border: '1px solid #D8DED4',
  background: '#fff',
  color: '#444',
  fontSize: 12,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
const BTN_PRIMARY = {
  padding: '8px 18px',
  borderRadius: 6,
  border: 'none',
  background: '#1B5E20',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

export function EntityFormModal({ title, fields, form, accounts = [], error, saving, onChange, onSubmit, onClose }) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      width={560}
      footer={
        <>
          <button type="button" style={BTN_GHOST} onClick={onClose}>
            Cancel
          </button>
          <button type="button" style={{ ...BTN_PRIMARY, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={onSubmit}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontFamily: "'Montserrat', sans-serif" }}
      >
        {fields.map((f) => (
          <div key={f.key} style={{ flex: f.full ? '1 1 100%' : '1 1 calc(50% - 6px)', minWidth: 200 }}>
            <label style={LABEL} htmlFor={`staffing-field-${f.key}`}>
              {f.label}
              {f.required && <span style={{ color: '#B71C1C' }}> *</span>}
            </label>

            {f.type === 'account' ? (
              <>
                <select
                  id={`staffing-field-${f.key}`}
                  value={form[f.key] ?? ''}
                  onChange={(e) => onChange(f.key, e.target.value)}
                  style={INPUT}
                >
                  <option value="">- Select account -</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
                {accounts.length === 0 && (
                  <div style={{ marginTop: 4, fontSize: 10.5, color: '#B98900' }}>No accounts yet - create an Account first.</div>
                )}
              </>
            ) : f.type === 'select' ? (
              <select
                id={`staffing-field-${f.key}`}
                value={form[f.key] ?? ''}
                onChange={(e) => onChange(f.key, e.target.value)}
                style={INPUT}
              >
                {!f.required && <option value="">- None -</option>}
                {(f.options || []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : f.type === 'textarea' ? (
              <textarea
                id={`staffing-field-${f.key}`}
                rows={3}
                value={form[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => onChange(f.key, e.target.value)}
                style={{ ...INPUT, resize: 'vertical' }}
              />
            ) : (
              <input
                id={`staffing-field-${f.key}`}
                type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                value={form[f.key] ?? ''}
                placeholder={f.placeholder}
                onChange={(e) => onChange(f.key, e.target.value)}
                style={INPUT}
              />
            )}
          </div>
        ))}
        {/* hidden submit so Enter in any field saves */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" />
      </form>

      {error && (
        <div
          style={{
            marginTop: 12,
            padding: '8px 12px',
            borderRadius: 6,
            background: '#FFEBEE',
            border: '1px solid #FFCDD2',
            fontSize: 11.5,
            fontWeight: 600,
            color: '#B71C1C',
          }}
        >
          {error}
        </div>
      )}
    </Modal>
  );
}
